<template>
  <ZDialog :show="show" modal-size="modal-lg">
    <template #header>
      <div class="row w-100">
        <div class="col-md-11">
          <h4 class="modal-title">Email Customer</h4>
        </div>
        <div class="col-md-1">
          <a href="#" class="custom-close" @click.prevent="close"><i class="fa fa-times"></i></a>
        </div>
      </div>
    </template>
    <template #body>
      <div v-if="loading" class="text-muted py-3">
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Preparing the email…
      </div>

      <div v-else-if="errorMessage" class="alert alert-warning py-2 mb-0">{{ errorMessage }}</div>

      <div v-else-if="result" class="alert py-2 mb-0" :class="result.success ? 'alert-success' : 'alert-danger'">
        {{ result.success ? `Sent to ${result.toEmail}.` : result.message }}
      </div>

      <div v-else>
        <div class="row g-2 mb-2">
          <div class="col-6">
            <label class="custom-label">To</label>
            <!-- A list, not one address: the report often needs to reach more than one person at the
                 shop. Each is a chip that can be taken off again. -->
            <div class="custom-recipients form-control form-control-sm" @click="focusRecipient">
              <span v-for="(address, i) in recipients" :key="address" class="custom-chip">
                {{ address }}
                <a href="#" class="custom-chip-remove" title="Remove this address"
                  @click.prevent.stop="removeRecipient(i)"><i class="fas fa-times"></i></a>
              </span>
              <input
                ref="recipient"
                v-model="recipientInput"
                class="custom-recipient-input"
                type="email"
                :placeholder="recipients.length ? 'add another' : 'email address'"
                @keydown.enter.prevent="commitRecipients"
                @keydown.,.prevent="commitRecipients"
                @keydown.delete="backspaceRecipient"
                @blur="commitRecipients"
              />
            </div>
          </div>
          <div class="col-6">
            <label class="custom-label">Subject</label>
            <input class="form-control form-control-sm" v-model="subject" />
          </div>
        </div>
        <label class="custom-label">Message</label>
        <!-- Edited in place. The body arrives assembled from the provider's template with the inspection
             merged in, and edits here are a one-off: the saved template is unchanged. Edited as rendered
             HTML so a template's formatting survives being touched. -->
        <div ref="body" class="custom-preview custom-editable" contenteditable="true" v-html="preview.html"></div>

        <!-- Shown but not editable: the sign-up callout is set once on the root firm and goes out below
             every one of these emails. -->
        <div v-if="preview.callout" class="mt-2">
          <label class="custom-label">Appended automatically</label>
          <div class="custom-preview custom-callout" v-html="preview.callout"></div>
        </div>

        <p class="text-muted small mb-0 mt-2">
          <i class="fas fa-paperclip me-1"></i>
          The ADAS Map report is attached as
          <strong>{{ preview.attachmentName }}</strong>.
          Edits here apply to this email only - change the wording for every email under
          Configuration &gt; ADAS Map Provider Customer Email.
          Placeholders such as [RONUMBER] and [VIN] work here too.
        </p>
      </div>
    </template>
    <template #footer>
      <button class="btn btn-secondary btn-sm" :disabled="sending" @click="close">
        {{ result ? 'Close' : 'Cancel' }}
      </button>
      <button
        v-if="!result && !errorMessage"
        class="btn btn-primary btn-sm ms-1"
        :disabled="sending || loading || (!recipients.length && !recipientInput)"
        @click="send"
      >
        <span v-if="sending" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        <i v-else class="fas fa-paper-plane me-1"></i>{{ sending ? 'Sending…' : 'Send' }}
      </button>
    </template>
  </ZDialog>
</template>
<script>
import { mapActions } from 'pinia'
import { customerStore } from '@/stores/customer'
import ZDialog from '@/components/utilities/elements/Dialog.vue'
export default {
  name: 'DialogEmailCustomer',
  components: { ZDialog },
  props: {
    show: { type: Boolean, default: false },
    source: { type: [String, Number], default: '' },
    adasInspection: { type: [String, Number], default: null }
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      sending: false,
      errorMessage: '',
      preview: {},
      recipients: [],
      recipientInput: '',
      subject: '',
      result: null
    }
  },
  watch: {
    show(open) {
      if (open) this.load()
    }
  },
  methods: {
    ...mapActions(customerStore, ['previewCustomerEmail', 'emailCustomer']),
    async load() {
      this.loading = true
      this.errorMessage = ''
      this.result = null
      try {
        this.preview = await this.previewCustomerEmail(this.source, this.adasInspection)
        this.recipients = this.splitAddresses(this.preview.toEmail)
        this.recipientInput = ''
        this.subject = this.preview.subject || ''
      } catch (e) {
        const d = e && e.response && e.response.data
        this.errorMessage = (d && (d.message || d.Message)) || 'The email could not be prepared.'
      } finally {
        this.loading = false
      }
    },
    splitAddresses(value) {
      return (value || '')
        .split(/[,;]/)
        .map((a) => a.trim())
        .filter((a) => a.length)
    },
    // Anything typed becomes chips: on Enter, on a comma, or when the field loses focus.
    commitRecipients() {
      const added = this.splitAddresses(this.recipientInput)
      added.forEach((address) => {
        if (!this.recipients.some((existing) => existing.toLowerCase() === address.toLowerCase())) {
          this.recipients.push(address)
        }
      })
      this.recipientInput = ''
    },
    removeRecipient(index) {
      this.recipients.splice(index, 1)
    },
    // Backspace in an empty box takes the last chip off, which is how every other chip field behaves.
    backspaceRecipient() {
      if (!this.recipientInput.length && this.recipients.length) this.recipients.pop()
    },
    focusRecipient() {
      if (this.$refs.recipient) this.$refs.recipient.focus()
    },
    async send() {
      this.sending = true
      this.commitRecipients()
      try {
        this.result = await this.emailCustomer({
          source: this.source,
          adasInspection: Number(this.adasInspection),
          toEmail: this.recipients.join(', '),
          subject: this.subject,
          // Read off the element rather than bound: two-way binding a contenteditable re-renders it on
          // every keystroke and moves the caret to the start.
          html: this.$refs.body ? this.$refs.body.innerHTML : null
        })
      } catch (e) {
        const d = e && e.response && e.response.data
        this.errorMessage = (d && (d.message || d.Message)) || 'The email could not be sent.'
      } finally {
        this.sending = false
      }
    },
    close() {
      this.preview = {}
      this.recipients = []
      this.recipientInput = ''
      this.result = null
      this.errorMessage = ''
      this.$emit('close')
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 2px;
}
.custom-recipients {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  height: auto;
  min-height: 31px;
  cursor: text;
}
.custom-chip {
  background-color: #e9ecef;
  border-radius: 12px;
  padding: 1px 8px;
  white-space: nowrap;
}
.custom-chip-remove {
  color: #6c757d;
  margin-left: 4px;
  text-decoration: none;
}
.custom-recipient-input {
  border: none;
  outline: none;
  flex: 1 1 120px;
  min-width: 100px;
  background: transparent;
}
.custom-preview {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.75rem;
  max-height: 320px;
  overflow-y: auto;
  background-color: #fff;
  font-size: 0.9rem;
}
.custom-editable:focus {
  outline: none;
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
}
.custom-callout {
  background-color: #f8f9fa;
  color: #6c757d;
}
.custom-close {
  color: #6c757d;
}
</style>
